/**
 * Nicht bestätigte KI-Vorschriften: Herkunft zeigen, zuweisen, bestätigen.
 *
 * Der gemini-proxy prüft jede Vorschrift, die die KI nennt
 * (supabase/functions/_shared/regelwerk.ts): feste Liste, Speicher, sonst
 * online an einer amtlichen Quelle. Was sich nicht bestätigen lässt, entfernt
 * er und meldet im Antwortkopf `x-regelwerke-verworfen` JEDE Stelle mit Pfad
 * (Eintrag + Feld). Hier wird der Kopf gelesen; `callAiBackend` legt dem
 * Nutzer die Stellen vor, BEVOR der Vorschlag beim Aufrufer ankommt
 * (components/KiRegelwerkHinweis.tsx). So braucht keiner der rund zwanzig
 * Aufrufer eine eigene Anzeige, und eine bestätigte Vorschrift landet genau
 * in dem Eintrag, den der Nutzer gewählt hat.
 *
 * Eine Bestätigung gilt nur für DIESEN Vorschlag. Sie schaltet die Vorschrift
 * nicht dauerhaft frei — sonst könnte ein einzelner Klick eine erfundene
 * Vorschrift für alle Nutzer und alle Dokumente freigeben.
 */

import { REGELWERK_FELDER, type PfadSchritt, type VerworfeneStelle } from '../../supabase/functions/_shared/regelwerk';

export type { PfadSchritt, VerworfeneStelle };

export const REGELWERK_KOPF = 'x-regelwerke-verworfen';
export const REGELWERK_EREIGNIS = 'os-ia:regelwerke-verworfen';

/** Was der Dialog bekommt: die Antwort, die Stellen und den Rückweg. */
export interface RegelwerkAnfrage {
    aktion: string;
    daten: unknown;
    stellen: VerworfeneStelle[];
    /** Mit der (ggf. ergänzten) Antwort aufrufen — genau einmal. */
    fertig: (daten: unknown) => void;
}

const istPfad = (v: unknown): v is PfadSchritt[] =>
    Array.isArray(v) && v.every(s => typeof s === 'string' || (typeof s === 'number' && Number.isInteger(s) && s >= 0));

/**
 * Kopf lesen. Kaputter oder fehlender Kopf = nichts verworfen. Die ältere
 * Proxy-Fassung schickte nur Namen — die kommen als nicht zuordenbare
 * Stellen an.
 */
export const leseVerworfeneRegelwerke = (kopf: string | null | undefined): VerworfeneStelle[] => {
    if (!kopf) return [];
    let roh: unknown;
    try {
        roh = JSON.parse(decodeURIComponent(kopf));
    } catch {
        return [];
    }
    if (!Array.isArray(roh)) return [];
    const stellen: VerworfeneStelle[] = [];
    for (const e of roh) {
        if (typeof e === 'string' && e.trim()) {
            stellen.push({ angabe: e.trim(), pfad: null, art: 'text' });
        } else if (e && typeof e === 'object') {
            const o = e as Record<string, unknown>;
            if (typeof o.angabe !== 'string' || !o.angabe.trim()) continue;
            stellen.push({
                angabe: o.angabe.trim(),
                pfad: istPfad(o.pfad) ? o.pfad : null,
                art: o.art === 'feld' ? 'feld' : 'text',
            });
        }
    }
    return stellen.slice(0, 30);
};

const wertAn = (daten: unknown, pfad: PfadSchritt[]): unknown =>
    pfad.reduce<unknown>((w, s) => (w !== null && typeof w === 'object' ? (w as Record<string | number, unknown>)[s] : undefined), daten);

/** Felder, die einen Eintrag für Menschen benennen — in dieser Reihenfolge. */
const BESCHRIFTUNG = ['bezeichnung', 'titel', 'title', 'component', 'name', 'gefaehrdung', 'thema', 'gewerk', 'activity'];

/**
 * Beschriftung des Eintrags, zu dem ein Feld gehört: „3. Gerüst aufbauen",
 * „Eintrag 3" oder „Vorschlag" (eine Antwort ohne Liste, z. B. ein Mängeltext).
 */
export const eintragFuer = (daten: unknown, feldPfad: PfadSchritt[]): string => {
    const eintragPfad = feldPfad.slice(0, -1);
    const eintrag = wertAn(daten, eintragPfad);
    let name = '';
    if (eintrag && typeof eintrag === 'object' && !Array.isArray(eintrag)) {
        const o = eintrag as Record<string, unknown>;
        const schluessel = Object.keys(o);
        for (const gesucht of BESCHRIFTUNG) {
            const k = schluessel.find(s => s.toLowerCase() === gesucht);
            const w = k ? o[k] : undefined;
            if (typeof w === 'string' && w.trim()) { name = w.trim().replace(/\s+/g, ' '); break; }
        }
    }
    if (name.length > 80) name = `${name.slice(0, 79)}…`;
    const index = [...eintragPfad].reverse().find((s): s is number => typeof s === 'number');
    if (index === undefined) return name || 'Vorschlag';
    return name ? `${index + 1}. ${name}` : `Eintrag ${index + 1}`;
};

/** Ein Regelwerksfeld der Antwort, in das eine Vorschrift gelegt werden kann. */
export interface RegelwerkZiel {
    pfad: PfadSchritt[];
    eintrag: string;
}

/** Alle Regelwerksfelder der Antwort (Text oder Liste) — die Ziele für „zuweisen". */
export const regelwerkZiele = (daten: unknown): RegelwerkZiel[] => {
    const ziele: RegelwerkZiel[] = [];
    const lauf = (wert: unknown, pfad: PfadSchritt[]) => {
        if (Array.isArray(wert)) {
            wert.forEach((e, i) => lauf(e, [...pfad, i]));
        } else if (wert && typeof wert === 'object') {
            for (const [k, v] of Object.entries(wert as Record<string, unknown>)) {
                if (k.startsWith('_')) continue;
                if (REGELWERK_FELDER.has(k.toLowerCase()) && (typeof v === 'string' || Array.isArray(v))) {
                    ziele.push({ pfad: [...pfad, k], eintrag: eintragFuer(daten, [...pfad, k]) });
                } else {
                    lauf(v, [...pfad, k]);
                }
            }
        }
    };
    lauf(daten, []);
    return ziele;
};

/** Feldnamen der Antwort für Menschen. */
const FELD_NAMEN: Record<string, string> = {
    vorschriften: 'Vorschriften',
    vorschrift: 'Vorschrift',
    regulation: 'Vorschrift',
    regulations: 'Vorschriften',
    regelwerkreferenz: 'Regelwerk',
    regelwerk: 'Regelwerk',
    regelwerke: 'Regelwerke',
    rechtsgrundlage: 'Rechtsgrundlage',
    rechtsgrundlagen: 'Rechtsgrundlagen',
    gesetz: 'Gesetz',
};

/**
 * Beschriftungen der Ziele für die Auswahl „zuweisen zu" — eindeutig: hat ein
 * Eintrag mehrere Regelwerksfelder, steht der Feldname dabei, und bleibt es
 * dann noch gleich, der Pfad (Codex-Review 13.10.).
 */
export const zielBeschriftungen = (ziele: RegelwerkZiel[]): string[] => {
    const zaehle = (texte: string[]) => texte.reduce((m, t) => m.set(t, (m.get(t) ?? 0) + 1), new Map<string, number>());
    const feld = (z: RegelwerkZiel) => {
        const k = String(z.pfad[z.pfad.length - 1] ?? '');
        return FELD_NAMEN[k.toLowerCase()] ?? k;
    };
    const stufe1 = ziele.map(z => z.eintrag);
    const n1 = zaehle(stufe1);
    const stufe2 = ziele.map((z, i) => (n1.get(stufe1[i])! > 1 ? `${z.eintrag} · ${feld(z)}` : z.eintrag));
    const n2 = zaehle(stufe2);
    return ziele.map((z, i) => (n2.get(stufe2[i])! > 1 ? `${stufe2[i]} (${z.pfad.join('.')})` : stufe2[i]));
};

const gleich = (a: PfadSchritt[], b: PfadSchritt[]) => a.length === b.length && a.every((s, i) => s === b[i]);

/**
 * Vorbelegtes Ziel einer Stelle: das Feld, aus dem sie stammt — bei einer
 * Nennung im Fließtext das Regelwerksfeld DESSELBEN Eintrags. `null`, wenn es
 * keines gibt; dann muss der Nutzer selbst zuweisen.
 */
export const zielFuerStelle = (daten: unknown, stelle: VerworfeneStelle): PfadSchritt[] | null => {
    if (!stelle.pfad) return null;
    const ziele = regelwerkZiele(daten);
    if (stelle.art === 'feld') {
        const pfad = stelle.pfad;
        return ziele.some(z => gleich(z.pfad, pfad)) ? pfad : null;
    }
    const eintragPfad = stelle.pfad.slice(0, -1);
    return ziele.find(z => gleich(z.pfad.slice(0, -1), eintragPfad))?.pfad ?? null;
};

/**
 * Für den Dialog: dieselbe Vorschrift aus Text UND Feld desselben Eintrags
 * nur einmal zeigen — die Feld-Stelle gewinnt, sie ist das natürliche Ziel.
 */
export const fasseStellenZusammen = (stellen: VerworfeneStelle[]): VerworfeneStelle[] => {
    const eintrag = (s: VerworfeneStelle) => `${s.angabe}|${s.pfad ? JSON.stringify(s.pfad.slice(0, -1)) : '-'}`;
    const mitFeld = new Set(stellen.filter(s => s.art === 'feld').map(eintrag));
    const gesehen = new Set<string>();
    return stellen.filter(s => {
        if (s.art === 'text' && mitFeld.has(eintrag(s))) return false;
        const k = `${eintrag(s)}|${s.art}`;
        if (gesehen.has(k)) return false;
        gesehen.add(k);
        return true;
    });
};

/** Herkunft einer Stelle für die Anzeige. */
export const herkunftFuer = (daten: unknown, stelle: VerworfeneStelle): string => {
    if (!stelle.pfad) return 'Herkunft nicht zuordenbar';
    const feld = String(stelle.pfad[stelle.pfad.length - 1] ?? '');
    const eintrag = eintragFuer(daten, stelle.pfad);
    return stelle.art === 'feld' ? eintrag : `${eintrag} (im Text „${feld}")`;
};

/**
 * Bestätigte Vorschriften an ihr Ziel legen — auf einer KOPIE der Antwort.
 * Liste: anhängen; Text: mit „, " anhängen; schon vorhanden: nichts tun.
 */
export const uebernimmBestaetigte = (
    daten: unknown,
    entscheidungen: { angabe: string; ziel: PfadSchritt[] }[],
): unknown => {
    if (entscheidungen.length === 0) return daten;
    const kopie: unknown = JSON.parse(JSON.stringify(daten));
    for (const { angabe, ziel } of entscheidungen) {
        if (ziel.length === 0) continue;
        const eltern = wertAn(kopie, ziel.slice(0, -1));
        if (!eltern || typeof eltern !== 'object') continue;
        const behaelter = eltern as Record<string | number, unknown>;
        const feld = ziel[ziel.length - 1];
        const jetzt = behaelter[feld];
        if (Array.isArray(jetzt)) {
            if (!jetzt.includes(angabe)) jetzt.push(angabe);
        } else if (typeof jetzt === 'string') {
            const teile = jetzt.split(',').map(t => t.trim()).filter(Boolean);
            if (!teile.includes(angabe)) behaelter[feld] = [...teile, angabe].join(', ');
        } else if (jetzt === undefined || jetzt === null) {
            behaelter[feld] = angabe;
        }
    }
    return kopie;
};

let dialogBereit = false;

/** Die Dialog-Komponente meldet sich an und ab (KiRegelwerkHinweis). */
export const meldeRegelwerkDialog = (bereit: boolean): void => {
    dialogBereit = bereit;
};

/**
 * Legt die verworfenen Stellen dem Nutzer vor und liefert die Antwort zurück —
 * unverändert oder um bestätigte Vorschriften ergänzt. Ohne Dialog (öffentliche
 * Seiten, Tests) kommt die Antwort sofort unverändert zurück.
 */
export const entscheideVerworfeneRegelwerke = <T,>(
    aktion: string,
    daten: T,
    stellen: VerworfeneStelle[],
): Promise<T> => {
    if (stellen.length === 0 || typeof window === 'undefined' || !dialogBereit) return Promise.resolve(daten);
    return new Promise<T>(resolve => {
        let erledigt = false;
        // Der Dialog ergänzt nur Werte in derselben Form — die Antwort bleibt vom Typ T.
        const fertig = (neu: unknown) => {
            if (erledigt) return;
            erledigt = true;
            resolve(neu as T);
        };
        window.dispatchEvent(new CustomEvent<RegelwerkAnfrage>(REGELWERK_EREIGNIS, { detail: { aktion, daten, stellen, fertig } }));
    });
};
